import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, forkJoin } from 'rxjs';
import {
  AccountAuthService,
  ApiResponse,
  ApiKeySummary,
  CatalogPermission,
  CatalogRole,
  ClientAccount,
  CreatedApiKey,
  DestructiveOpsPasswordStatus,
  ValidationEmailEntry,
  WhitelistEntry,
  WhitelistRole,
} from '../../shared/account-auth.service';
import { ClickOutsideDirective } from '../../shared/click-outside.directive';

type DashboardPanel = {
  id: string;
  label: string;
};

type DashboardTable = {
  title: string;
  columns: string[];
  rows: Record<string, unknown>[];
};

const DASHBOARD_PANELS: Record<string, DashboardPanel[]> = {
  account_self_service: [{ id: 'account-profile', label: 'Profile' }],
  user: [{ id: 'api-key', label: 'API Key' }],
  admin: [
    { id: 'service-operations', label: 'Service Operations' },
    { id: 'destructive-password', label: 'Destructive Password' },
    { id: 'validation', label: 'Validation' },
    { id: 'whitelist', label: 'Whitelist' },
    { id: 'client-accounts', label: 'Client Accounts' },
    { id: 'roles', label: 'Roles' },
    { id: 'permissions', label: 'Permissions' },
    { id: 'api-keys', label: 'API Keys' },
  ],
};

@Component({
  selector: 'app-account-dashboard',
  imports: [CommonModule, ReactiveFormsModule, RouterModule, ClickOutsideDirective],
  templateUrl: './account-dashboard.html',
  styleUrl: './account-dashboard.scss',
})
export class AccountDashboard implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AccountAuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly user = this.auth.user;
  readonly roles = this.auth.roles;
  readonly permissions = this.auth.permissions;
  readonly dashboardRole = signal('account_self_service');
  readonly activePanel = signal('');
  readonly dashboardPanels = computed(
    () => DASHBOARD_PANELS[this.dashboardRole()] ?? []
  );
  readonly plans = [
    { id: 1, name: 'Free', crypts: '1,000 crypts / month' },
    { id: 2, name: 'Basic', crypts: '5,000 crypts / month' },
    { id: 3, name: 'Pro', crypts: '25,000 crypts / month' },
    { id: 4, name: 'Enterprise', crypts: '100,000 crypts / month' },
  ];
  readonly apiKeySummary = signal<ApiKeySummary | null>(null);
  readonly apiKeySummaryLoading = signal(false);
  readonly apiKeyUsagePercent = computed(() => {
    const summary = this.apiKeySummary();
    const allowance = summary?.plan.cryptsPerMonth;
    if (!allowance || allowance <= 0) {
      return null;
    }
    return Math.min(
      100,
      Math.round((summary.usage.cryptsUsedThisMonth / allowance) * 100)
    );
  });

  readonly selfServiceForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    email: ['', [Validators.required, Validators.email]],
    monthlyPlanId: [1, [Validators.required]],
    oldPassword: ['', [Validators.required, Validators.minLength(8)]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', [Validators.required, Validators.minLength(8)]],
  });

  readonly apiKeyForm = this.fb.nonNullable.group({
    description: ['', [Validators.maxLength(255)]],
    expiresIn: ['1m', [Validators.required]],
  });
  readonly expiresOptions = [
    { value: '24h', label: '24 hours' },
    { value: '1w', label: '1 week' },
    { value: '1m', label: '1 month' },
    { value: '3m', label: '3 months' },
  ] as const;
  expiresOpen = false;

  readonly rotationForm = this.fb.nonNullable.group({
    operationsPassword: ['', [Validators.required, Validators.minLength(8)]],
    rotationPassword: ['', [Validators.required]],
    force: [false],
  });

  readonly destructivePasswordRotateForm = this.fb.nonNullable.group({
    oldOperationsPassword: ['', [Validators.required, Validators.minLength(8)]],
    newOperationsPassword: ['', [Validators.required, Validators.minLength(8)]],
    confirmNewOperationsPassword: [
      '',
      [Validators.required, Validators.minLength(8)],
    ],
  });

  readonly whitelistCreateForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  readonly whitelistUpdateForm = this.fb.nonNullable.group({
    uuid: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
  });

  readonly whitelistFilterForm = this.fb.nonNullable.group({
    email: [''],
    role: [''],
  });

  readonly clientPatchForm = this.fb.nonNullable.group({
    uuid: ['', [Validators.required]],
    name: ['', [Validators.required, Validators.maxLength(255)]],
    email: ['', [Validators.required, Validators.email]],
    verified: [false],
    monthlyPlanId: [1 as number | null],
    reqs: [0 as number | null],
    monthlyReqs: [0 as number | null],
    cycleStartAt: [''],
    operationsPassword: [''],
  });

  readonly apiKeyLookupForm = this.fb.nonNullable.group({
    keyId: ['', [Validators.required]],
    operationsPassword: [''],
  });

  readonly apiKeysPageForm = this.fb.nonNullable.group({
    page: [1, [Validators.required]],
  });

  readonly roleCreateForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
  });

  readonly roleUpdateForm = this.fb.nonNullable.group({
    uuid: ['', [Validators.required]],
    name: ['', [Validators.required, Validators.maxLength(255)]],
  });

  readonly roleSearchForm = this.fb.nonNullable.group({
    search: [''],
  });

  readonly permissionCreateForm = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
  });

  readonly permissionUpdateForm = this.fb.nonNullable.group({
    uuid: ['', [Validators.required]],
    name: ['', [Validators.required, Validators.maxLength(255)]],
  });

  readonly permissionSearchForm = this.fb.nonNullable.group({
    search: [''],
  });

  loadingAction = '';
  createdApiKey: CreatedApiKey | null = null;
  apiKeyRevealed = false;
  dashboardResults: Record<string, unknown> = {};
  isSelfServiceEditing = false;
  private selfServiceSnapshot = {
    name: '',
    email: '',
    monthlyPlanId: 1,
  };

  private _successMessage = '';
  private _errorMessage = '';
  private _copyMessage = '';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  get successMessage() {
    return this._successMessage;
  }

  set successMessage(value: string) {
    this._successMessage = value;
    if (value) {
      this.presentToast(value, 'success');
    }
  }

  get errorMessage() {
    return this._errorMessage;
  }

  set errorMessage(value: string) {
    this._errorMessage = value;
    if (value) {
      this.presentToast(value, 'error');
    }
  }

  get copyMessage() {
    return this._copyMessage;
  }

  set copyMessage(value: string) {
    this._copyMessage = value;
    if (value) {
      this.presentToast(
        value,
        value.toLowerCase().includes('unavailable') ? 'error' : 'success'
      );
    }
  }

  readonly whitelistEntries = signal<WhitelistEntry[]>([]);
  readonly whitelistRoles = signal<WhitelistRole[]>([]);
  readonly whitelistTotal = signal(0);
  readonly whitelistPage = signal(1);
  readonly whitelistPageSize = 10;
  readonly whitelistLoading = signal(false);
  createRoleUuids = new Set<string>();
  editRoleUuids = new Set<string>();
  createRolesOpen = false;
  editRolesOpen = false;
  editingWhitelistUuid: string | null = null;
  pendingWhitelistDelete: WhitelistEntry | null = null;
  private whitelistSearchTimer: ReturnType<typeof setTimeout> | null = null;

  readonly validationEntries = signal<ValidationEmailEntry[]>([]);
  readonly validationTotal = signal(0);
  readonly validationPage = signal(1);
  readonly validationPageSize = 25;
  readonly validationLoading = signal(false);
  enrollingValidationEmail: string | null = null;

  readonly clientAccounts = signal<ClientAccount[]>([]);
  readonly clientAvailableRoles = signal<WhitelistRole[]>([]);
  readonly clientAccountsTotal = signal(0);
  readonly clientAccountsPage = signal(1);
  readonly clientAccountsPageSize = 10;
  readonly clientAccountsLoading = signal(false);
  editingClientAccountUuid: string | null = null;
  editClientRoleUuids = new Set<string>();
  editClientRolesOpen = false;

  readonly catalogRoles = signal<CatalogRole[]>([]);
  readonly availablePermissions = signal<CatalogPermission[]>([]);
  readonly catalogRolesTotal = signal(0);
  readonly catalogRolesPage = signal(1);
  readonly catalogRolesPageSize = 10;
  readonly catalogRolesLoading = signal(false);
  editingCatalogRoleUuid: string | null = null;
  createPermissionUuids = new Set<string>();
  editPermissionUuids = new Set<string>();
  createPermissionsOpen = false;
  editPermissionsOpen = false;
  private rolesSearchTimer: ReturnType<typeof setTimeout> | null = null;

  readonly catalogPermissions = signal<CatalogPermission[]>([]);
  readonly catalogPermissionsTotal = signal(0);
  readonly catalogPermissionsPage = signal(1);
  readonly catalogPermissionsPageSize = 10;
  readonly catalogPermissionsLoading = signal(false);
  editingCatalogPermissionUuid: string | null = null;
  private permissionsSearchTimer: ReturnType<typeof setTimeout> | null = null;

  readonly serviceStatus = signal<unknown>(null);
  readonly serviceStatusLoading = signal(false);
  readonly destructivePasswordStatus =
    signal<DestructiveOpsPasswordStatus | null>(null);
  readonly destructivePasswordStatusLoading = signal(false);

  ngOnInit() {
    this.route.data.subscribe((data) => {
      this.dashboardRole.set(
        typeof data['dashboardRole'] === 'string'
          ? data['dashboardRole']
          : 'account_self_service'
      );
      const panelId = this.dashboardPanels()[0]?.id ?? '';
      this.activePanel.set(panelId);
      if (this.dashboardRole() === 'user') {
        this.loadApiKeySummary();
      }
      this.activatePanelData(panelId);
    });

    this.auth.refreshAccess().subscribe({
      next: () => {
        this.syncSelfServiceForm();
        this.redirectIfDashboardUnavailable();
      },
      error: () => {
        // Invalid JWT is cleared and redirected by auth handling.
      },
    });
  }

  private redirectIfDashboardUnavailable() {
    const dashboards = this.auth.dashboards();
    if (dashboards.length > 0 && !this.roles().includes(this.dashboardRole())) {
      this.router.navigateByUrl(this.auth.defaultDashboardPath());
    }
  }

  selectPanel(panelId: string) {
    this.activePanel.set(panelId);
    this.successMessage = '';
    this.errorMessage = '';
    this.hideToast();
    this.dashboardResults = {};
    this.closeRoleDropdowns();
    this.closeExpiresDropdown();
    this.cancelEditClientAccount();
    this.cancelEditCatalogRole();
    this.cancelEditCatalogPermission();
    this.activatePanelData(panelId);
  }

  presentToast(message: string, type: 'success' | 'error') {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastTimer = setTimeout(() => this.hideToast(), 4000);
  }

  hideToast() {
    this.toastVisible = false;
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  private activatePanelData(panelId: string) {
    this.serviceStatus.set(null);
    this.destructivePasswordStatus.set(null);

    if (panelId === 'service-operations') {
      this.loadServiceStatus();
    }
    if (panelId === 'destructive-password') {
      this.loadDestructiveOpsPasswordStatus();
    }
    if (panelId === 'whitelist') {
      this.whitelistPage.set(1);
      this.loadWhitelist();
    }
    if (panelId === 'validation') {
      this.validationPage.set(1);
      this.loadValidationEmails();
      this.ensureWhitelistRoles();
    }
    if (panelId === 'client-accounts') {
      this.clientAccountsPage.set(1);
      this.loadClientAccounts();
    }
    if (panelId === 'roles') {
      this.catalogRolesPage.set(1);
      this.loadRolesCatalog(1);
    }
    if (panelId === 'permissions') {
      this.catalogPermissionsPage.set(1);
      this.loadPermissionsCatalog(1);
    }
  }

  startSelfServiceEdit() {
    this.isSelfServiceEditing = true;
    this.successMessage = '';
    this.errorMessage = '';
  }

  cancelSelfServiceEdit() {
    this.isSelfServiceEditing = false;
    this.syncSelfServiceForm();
    this.clearPasswordFields();
    this.successMessage = '';
    this.errorMessage = '';
  }

  saveSelfService() {
    if (this.loadingAction) {
      return;
    }

    const { name, email, monthlyPlanId, oldPassword, password, confirmPassword } =
      this.selfServiceForm.getRawValue();
    const nameChanged = name.trim() !== this.selfServiceSnapshot.name;
    const emailChanged =
      email.trim().toLowerCase() !== this.selfServiceSnapshot.email;
    const planChanged =
      Number(monthlyPlanId) !== Number(this.selfServiceSnapshot.monthlyPlanId);
    const passwordTouched = !!oldPassword || !!password || !!confirmPassword;

    const controlsToValidate = [];
    if (nameChanged) controlsToValidate.push(this.selfServiceForm.controls.name);
    if (emailChanged) controlsToValidate.push(this.selfServiceForm.controls.email);
    if (planChanged) {
      controlsToValidate.push(this.selfServiceForm.controls.monthlyPlanId);
    }
    if (passwordTouched) {
      controlsToValidate.push(
        this.selfServiceForm.controls.oldPassword,
        this.selfServiceForm.controls.password,
        this.selfServiceForm.controls.confirmPassword
      );
      if (password !== confirmPassword) {
        this.selfServiceForm.controls.confirmPassword.setErrors({ mismatch: true });
        this.selfServiceForm.controls.confirmPassword.markAsTouched();
        this.errorMessage = 'New passwords do not match.';
        return;
      }
    }

    for (const control of controlsToValidate) {
      if (control.invalid) {
        control.markAsTouched();
      }
    }
    if (controlsToValidate.some((control) => control.invalid)) {
      return;
    }

    const updates: Observable<unknown>[] = [];
    if (nameChanged) updates.push(this.auth.updateName(name.trim()));
    if (emailChanged) updates.push(this.auth.updateEmail(email.trim()));
    if (planChanged) updates.push(this.auth.updatePlan(Number(monthlyPlanId)));
    if (passwordTouched) {
      updates.push(this.auth.updatePassword(oldPassword, password));
    }

    if (updates.length === 0) {
      this.successMessage = 'No changes to save.';
      return;
    }

    this.loadingAction = 'self-service-save';
    this.successMessage = '';
    this.errorMessage = '';
    forkJoin(updates).subscribe({
      next: () => {
        this.loadingAction = '';
        this.isSelfServiceEditing = false;
        this.syncSelfServiceForm();
        this.clearPasswordFields();
        this.successMessage = emailChanged
          ? 'Changes saved. Check your inbox for a new verification passcode.'
          : 'Changes saved.';
      },
      error: (message: string) => {
        this.loadingAction = '';
        this.errorMessage = message;
      },
    });
  }

  createApiKey() {
    if (this.apiKeyForm.invalid || this.loadingAction) {
      this.apiKeyForm.markAllAsTouched();
      return;
    }

    this.loadingAction = 'api-key';
    this.successMessage = '';
    this.errorMessage = '';
    this.createdApiKey = null;
    this.apiKeyRevealed = false;
    this.copyMessage = '';
    const { description, expiresIn } = this.apiKeyForm.getRawValue();

    this.auth.createApiKey(description, expiresIn).subscribe({
      next: (response) => {
        this.loadingAction = '';
        this.createdApiKey = response.data ?? null;
        this.apiKeyRevealed = false;
        this.successMessage =
          response.message || 'API key created. Store it securely now.';
        this.apiKeyForm.controls.description.reset('');
        this.loadApiKeySummary();
      },
      error: (message: string) => {
        this.loadingAction = '';
        this.errorMessage = message;
      },
    });
  }

  loadApiKeySummary(showError = false) {
    if (this.apiKeySummaryLoading()) {
      return;
    }

    this.apiKeySummaryLoading.set(true);
    this.auth.getApiKeySummary().subscribe({
      next: (response) => {
        this.apiKeySummaryLoading.set(false);
        this.apiKeySummary.set(response.data ?? null);
      },
      error: (message: string) => {
        this.apiKeySummaryLoading.set(false);
        if (showError) {
          this.errorMessage = message;
        }
      },
    });
  }

  formatDateTime(value?: string | null) {
    if (!value) {
      return 'Not available';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'Not available';
    }

    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  }

  formatNumber(value?: number | null) {
    if (value == null) {
      return 'Unlimited';
    }
    return new Intl.NumberFormat().format(value);
  }

  displayedApiKey() {
    const key = this.createdApiKey?.apiKey ?? '';
    if (!key || this.apiKeyRevealed || key.length <= 6) {
      return key;
    }
    return `${key.slice(0, 6)}${'*'.repeat(key.length - 6)}`;
  }

  toggleApiKeyVisibility() {
    this.apiKeyRevealed = !this.apiKeyRevealed;
  }

  copyApiKey() {
    const key = this.createdApiKey?.apiKey;
    if (!key || typeof navigator === 'undefined' || !navigator.clipboard) {
      this.copyMessage = 'Copy unavailable in this browser.';
      return;
    }

    navigator.clipboard.writeText(key).then(() => {
      this.copyMessage = 'Copied.';
    });
  }

  loadServiceStatus() {
    this.serviceStatusLoading.set(true);
    this.auth.getServiceStatus().subscribe({
      next: (response) => {
        this.serviceStatusLoading.set(false);
        if (this.activePanel() !== 'service-operations') {
          return;
        }
        this.serviceStatus.set(response);
      },
      error: (message: string) => {
        this.serviceStatusLoading.set(false);
        if (this.activePanel() !== 'service-operations') {
          return;
        }
        this.errorMessage = message;
      },
    });
  }

  rotateKeyEncryptionKey() {
    const body = this.rotationForm.getRawValue();
    this.runDashboardAction('rotate-key-encryption-key', () =>
      this.auth.rotateKeyEncryptionKey(body)
    );
  }

  forceFreshStart() {
    const { operationsPassword, rotationPassword } = this.rotationForm.getRawValue();
    this.runDashboardAction('force-fresh-start', () =>
      this.auth.forceFreshStart({ operationsPassword, rotationPassword })
    );
  }

  loadDestructiveOpsPasswordStatus() {
    this.destructivePasswordStatusLoading.set(true);
    this.auth.getDestructiveOpsPasswordStatus().subscribe({
      next: (response) => {
        this.destructivePasswordStatusLoading.set(false);
        if (this.activePanel() !== 'destructive-password') {
          return;
        }
        this.destructivePasswordStatus.set(response.data ?? null);
      },
      error: (message: string) => {
        this.destructivePasswordStatusLoading.set(false);
        if (this.activePanel() !== 'destructive-password') {
          return;
        }
        this.errorMessage = message;
      },
    });
  }

  rotateDestructiveOpsPassword() {
    if (this.destructivePasswordRotateForm.invalid || this.loadingAction) {
      this.destructivePasswordRotateForm.markAllAsTouched();
      return;
    }

    const {
      oldOperationsPassword,
      newOperationsPassword,
      confirmNewOperationsPassword,
    } = this.destructivePasswordRotateForm.getRawValue();

    if (newOperationsPassword !== confirmNewOperationsPassword) {
      this.errorMessage = 'New password and confirmation do not match.';
      return;
    }

    this.loadingAction = 'destructive-password-rotate';
    this.successMessage = '';
    this.errorMessage = '';

    this.auth
      .rotateDestructiveOpsPassword(
        oldOperationsPassword,
        newOperationsPassword
      )
      .subscribe({
        next: (response) => {
          this.loadingAction = '';
          this.successMessage =
            response.message || 'Destructive operations password rotated.';
          this.destructivePasswordRotateForm.reset({
            oldOperationsPassword: '',
            newOperationsPassword: '',
            confirmNewOperationsPassword: '',
          });
          this.loadDestructiveOpsPasswordStatus();
        },
        error: (message: string) => {
          this.loadingAction = '';
          this.errorMessage = message;
        },
      });
  }

  loadWhitelist(page = this.whitelistPage()) {
    if (this.whitelistLoading()) {
      return;
    }

    const filters = this.whitelistFilterForm.getRawValue();
    this.whitelistLoading.set(true);
    this.loadingAction = 'whitelist';
    this.errorMessage = '';

    this.auth
      .listWhitelist({
        page,
        pageSize: this.whitelistPageSize,
        email: filters.email,
        role: filters.role,
      })
      .subscribe({
        next: (response) => {
          this.whitelistLoading.set(false);
          this.loadingAction = '';
          const total = response.count ?? 0;
          const responsePage = response.page ?? page;
          const totalPages = Math.max(1, Math.ceil(total / this.whitelistPageSize));
          if (responsePage > totalPages) {
            this.loadWhitelist(totalPages);
            return;
          }
          this.whitelistEntries.set(response.data ?? []);
          this.whitelistTotal.set(total);
          this.whitelistPage.set(responsePage);
          if (response.availableRoles) {
            this.whitelistRoles.set(response.availableRoles);
          }
        },
        error: (message: string) => {
          this.whitelistLoading.set(false);
          this.loadingAction = '';
          this.errorMessage = message;
        },
      });
  }

  onWhitelistEmailSearch() {
    if (this.whitelistSearchTimer) {
      clearTimeout(this.whitelistSearchTimer);
    }
    this.whitelistSearchTimer = setTimeout(() => {
      this.whitelistPage.set(1);
      this.loadWhitelist(1);
    }, 300);
  }

  onWhitelistRoleFilterChange() {
    this.whitelistPage.set(1);
    this.loadWhitelist(1);
  }

  whitelistTotalPages() {
    return Math.max(1, Math.ceil(this.whitelistTotal() / this.whitelistPageSize));
  }

  canGoWhitelistPrev() {
    return this.whitelistPage() > 1;
  }

  canGoWhitelistNext() {
    return this.whitelistPage() < this.whitelistTotalPages();
  }

  goWhitelistPage(delta: number) {
    const next = this.whitelistPage() + delta;
    if (next < 1 || next > this.whitelistTotalPages()) {
      return;
    }
    this.loadWhitelist(next);
  }

  loadValidationEmails(page = this.validationPage()) {
    if (this.validationLoading()) {
      return;
    }

    this.validationLoading.set(true);
    this.loadingAction = 'validation-emails';
    this.errorMessage = '';

    this.auth
      .listValidationEmails({
        page,
        pageSize: this.validationPageSize,
      })
      .subscribe({
        next: (response) => {
          this.validationLoading.set(false);
          this.loadingAction = '';
          const payload = response.data;
          const total = payload?.count ?? 0;
          const responsePage = payload?.page ?? page;
          const totalPages = Math.max(
            1,
            Math.ceil(total / this.validationPageSize)
          );
          if (responsePage > totalPages) {
            this.loadValidationEmails(totalPages);
            return;
          }
          this.validationEntries.set(payload?.entries ?? []);
          this.validationTotal.set(total);
          this.validationPage.set(responsePage);
        },
        error: (message: string) => {
          this.validationLoading.set(false);
          this.loadingAction = '';
          this.errorMessage = message;
          this.validationEntries.set([]);
        },
      });
  }

  private ensureWhitelistRoles() {
    if (this.whitelistRoles().length > 0) {
      return;
    }

    this.auth.listWhitelist({ page: 1, pageSize: 1 }).subscribe({
      next: (response) => {
        if (response.availableRoles) {
          this.whitelistRoles.set(response.availableRoles);
        }
      },
      error: () => {
        // Enroll button will surface a clearer error if roles stay empty.
      },
    });
  }

  validationTotalPages() {
    return Math.max(
      1,
      Math.ceil(this.validationTotal() / this.validationPageSize)
    );
  }

  canGoValidationPrev() {
    return this.validationPage() > 1;
  }

  canGoValidationNext() {
    return this.validationPage() < this.validationTotalPages();
  }

  goValidationPage(delta: number) {
    const next = this.validationPage() + delta;
    if (next < 1 || next > this.validationTotalPages()) {
      return;
    }
    this.loadValidationEmails(next);
  }

  formatBool(value?: boolean | null) {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '—';
  }

  formatSurveyNumber(value?: number | null) {
    if (value == null || Number.isNaN(Number(value))) {
      return '—';
    }
    return Number(value).toLocaleString();
  }

  formatSurveySummary(entry: ValidationEmailEntry) {
    const survey = entry.survey;
    const answered = [
      survey.q1,
      survey.q2,
      survey.q3,
      survey.q4,
      survey.q5,
      survey.q6,
    ].some((value) => value !== null && value !== undefined && value !== '');
    if (!answered) {
      return survey.opened ? 'Opened, no answers' : 'Not started';
    }
    return 'Completed / in progress';
  }

  hasSurveyDetails(entry: ValidationEmailEntry) {
    const survey = entry.survey;
    return [
      survey.q1,
      survey.q2,
      survey.q3,
      survey.q4,
      survey.q5,
      survey.q6,
    ].some((value) => value !== null && value !== undefined && value !== '');
  }

  formatBetaTester(entry: ValidationEmailEntry) {
    if (entry.betaTester?.withdrawn) {
      return 'Withdrawn';
    }
    if (entry.betaTester?.applied) {
      return 'Applied';
    }
    return 'Not applied';
  }

  canEnrollValidationEmail() {
    return this.permissions().includes('edit_account_whitelist');
  }

  defaultWhitelistRoleUuids(): string[] {
    const preferred = ['account_self_service', 'user'];
    const roles = this.whitelistRoles();
    const selected = preferred
      .map((name) => roles.find((role) => role.name === name)?.uuid)
      .filter((uuid): uuid is string => !!uuid);
    if (selected.length > 0) {
      return selected;
    }
    return roles[0]?.uuid ? [roles[0].uuid] : [];
  }

  enrollValidationEmail(entry: ValidationEmailEntry) {
    if (
      !entry?.email ||
      entry.whitelisted ||
      this.loadingAction ||
      !this.canEnrollValidationEmail()
    ) {
      return;
    }

    const roles = this.defaultWhitelistRoleUuids();
    if (roles.length === 0) {
      this.errorMessage =
        'No whitelist roles are available. Open the Whitelist tab once, then try again.';
      return;
    }

    this.enrollingValidationEmail = entry.email;
    this.loadingAction = 'validation-enroll';
    this.errorMessage = '';
    this.successMessage = '';

    this.auth
      .createWhitelistEntry({
        email: entry.email,
        roles,
      })
      .subscribe({
        next: () => {
          this.enrollingValidationEmail = null;
          this.loadingAction = '';
          this.successMessage = `${entry.email} enrolled in the whitelist.`;
          this.presentToast(this.successMessage, 'success');
          this.loadValidationEmails(this.validationPage());
        },
        error: (message: string) => {
          this.enrollingValidationEmail = null;
          this.loadingAction = '';
          this.errorMessage = message;
          this.presentToast(message, 'error');
        },
      });
  }

  roleNames(entry: WhitelistEntry) {
    return entry.roles.map((role) => role.name).join(', ');
  }

  formatWhitelistDate(value?: string | null) {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const meridiem = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    return `${month}/${day}/${year} ${hours}:${minutes} ${meridiem}`;
  }

  formatAdminDateTime(value?: string | null) {
    return this.formatWhitelistDate(value);
  }

  formatAdminDate(value?: string | null) {
    if (!value) {
      return '';
    }
    const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  selectedRolesLabel(selected: Set<string>) {
    const names = this.whitelistRoles()
      .filter((role) => selected.has(role.uuid))
      .map((role) => role.name);
    if (names.length === 0) {
      return 'Select roles';
    }
    return names.join(', ');
  }

  selectedClientRolesLabel(selected: Set<string>) {
    const names = this.clientAvailableRoles()
      .filter((role) => selected.has(role.uuid))
      .map((role) => role.name);
    if (names.length === 0) {
      return 'Select roles';
    }
    return names.join(', ');
  }

  selectedPermissionsLabel(selected: Set<string>) {
    const names = this.availablePermissions()
      .filter((permission) => selected.has(permission.uuid))
      .map((permission) => permission.name);
    if (names.length === 0) {
      return 'Select permissions';
    }
    return names.join(', ');
  }

  toggleCreateRole(roleUuid: string) {
    if (this.createRoleUuids.has(roleUuid)) {
      this.createRoleUuids.delete(roleUuid);
    } else {
      this.createRoleUuids.add(roleUuid);
    }
    this.createRoleUuids = new Set(this.createRoleUuids);
  }

  toggleEditRole(roleUuid: string) {
    if (this.editRoleUuids.has(roleUuid)) {
      this.editRoleUuids.delete(roleUuid);
    } else {
      this.editRoleUuids.add(roleUuid);
    }
    this.editRoleUuids = new Set(this.editRoleUuids);
  }

  toggleEditClientRole(roleUuid: string) {
    if (this.editClientRoleUuids.has(roleUuid)) {
      this.editClientRoleUuids.delete(roleUuid);
    } else {
      this.editClientRoleUuids.add(roleUuid);
    }
    this.editClientRoleUuids = new Set(this.editClientRoleUuids);
  }

  toggleCreatePermission(permissionUuid: string) {
    if (this.createPermissionUuids.has(permissionUuid)) {
      this.createPermissionUuids.delete(permissionUuid);
    } else {
      this.createPermissionUuids.add(permissionUuid);
    }
    this.createPermissionUuids = new Set(this.createPermissionUuids);
  }

  toggleEditPermission(permissionUuid: string) {
    if (this.editPermissionUuids.has(permissionUuid)) {
      this.editPermissionUuids.delete(permissionUuid);
    } else {
      this.editPermissionUuids.add(permissionUuid);
    }
    this.editPermissionUuids = new Set(this.editPermissionUuids);
  }

  isCreateRoleSelected(roleUuid: string) {
    return this.createRoleUuids.has(roleUuid);
  }

  isEditRoleSelected(roleUuid: string) {
    return this.editRoleUuids.has(roleUuid);
  }

  isEditClientRoleSelected(roleUuid: string) {
    return this.editClientRoleUuids.has(roleUuid);
  }

  isCreatePermissionSelected(permissionUuid: string) {
    return this.createPermissionUuids.has(permissionUuid);
  }

  isEditPermissionSelected(permissionUuid: string) {
    return this.editPermissionUuids.has(permissionUuid);
  }

  toggleCreateRolesOpen() {
    this.createRolesOpen = !this.createRolesOpen;
    if (this.createRolesOpen) {
      this.editRolesOpen = false;
      this.editClientRolesOpen = false;
      this.createPermissionsOpen = false;
      this.editPermissionsOpen = false;
      this.expiresOpen = false;
    }
  }

  toggleEditRolesOpen() {
    this.editRolesOpen = !this.editRolesOpen;
    if (this.editRolesOpen) {
      this.createRolesOpen = false;
      this.editClientRolesOpen = false;
      this.createPermissionsOpen = false;
      this.editPermissionsOpen = false;
      this.expiresOpen = false;
    }
  }

  toggleEditClientRolesOpen() {
    this.editClientRolesOpen = !this.editClientRolesOpen;
    if (this.editClientRolesOpen) {
      this.createRolesOpen = false;
      this.editRolesOpen = false;
      this.createPermissionsOpen = false;
      this.editPermissionsOpen = false;
      this.expiresOpen = false;
    }
  }

  toggleCreatePermissionsOpen() {
    this.createPermissionsOpen = !this.createPermissionsOpen;
    if (this.createPermissionsOpen) {
      this.createRolesOpen = false;
      this.editRolesOpen = false;
      this.editClientRolesOpen = false;
      this.editPermissionsOpen = false;
      this.expiresOpen = false;
    }
  }

  toggleEditPermissionsOpen() {
    this.editPermissionsOpen = !this.editPermissionsOpen;
    if (this.editPermissionsOpen) {
      this.createRolesOpen = false;
      this.editRolesOpen = false;
      this.editClientRolesOpen = false;
      this.createPermissionsOpen = false;
      this.expiresOpen = false;
    }
  }

  closeRoleDropdowns() {
    this.createRolesOpen = false;
    this.editRolesOpen = false;
    this.editClientRolesOpen = false;
    this.createPermissionsOpen = false;
    this.editPermissionsOpen = false;
  }

  expiresInLabel() {
    const value = this.apiKeyForm.controls.expiresIn.value;
    return (
      this.expiresOptions.find((option) => option.value === value)?.label ??
      'Select expiry'
    );
  }

  toggleExpiresOpen() {
    this.expiresOpen = !this.expiresOpen;
    if (this.expiresOpen) {
      this.closeRoleDropdowns();
    }
  }

  selectExpiresIn(value: string) {
    this.apiKeyForm.controls.expiresIn.setValue(value);
    this.expiresOpen = false;
  }

  closeExpiresDropdown() {
    this.expiresOpen = false;
  }

  startEditWhitelist(entry: WhitelistEntry) {
    this.editingWhitelistUuid = entry.uuid;
    this.whitelistUpdateForm.setValue({
      uuid: entry.uuid,
      email: entry.email,
    });
    this.editRoleUuids = new Set(entry.roles.map((role) => role.uuid));
    this.editRolesOpen = false;
    this.createRolesOpen = false;
    this.successMessage = '';
    this.errorMessage = '';
  }

  cancelEditWhitelist() {
    this.editingWhitelistUuid = null;
    this.editRoleUuids = new Set();
    this.editRolesOpen = false;
    this.whitelistUpdateForm.reset({ uuid: '', email: '' });
  }

  createWhitelistEntry() {
    if (this.whitelistCreateForm.invalid || this.loadingAction) {
      this.whitelistCreateForm.markAllAsTouched();
      return;
    }
    if (this.createRoleUuids.size === 0) {
      this.errorMessage = 'Select at least one role.';
      return;
    }

    const { email } = this.whitelistCreateForm.getRawValue();
    this.runWhitelistMutation(
      'whitelist-create',
      () =>
        this.auth.createWhitelistEntry({
          email,
          roles: [...this.createRoleUuids],
        }),
      'Whitelist entry added.',
      () => {
        this.whitelistCreateForm.reset({ email: '' });
        this.createRoleUuids = new Set();
        this.createRolesOpen = false;
      }
    );
  }

  updateWhitelistEntry() {
    if (!this.editingWhitelistUuid || this.whitelistUpdateForm.invalid) {
      this.whitelistUpdateForm.markAllAsTouched();
      return;
    }
    if (this.editRoleUuids.size === 0) {
      this.errorMessage = 'Select at least one role.';
      return;
    }

    const { uuid, email } = this.whitelistUpdateForm.getRawValue();
    this.runWhitelistMutation(
      'whitelist-update',
      () =>
        this.auth.updateWhitelistEntry(uuid, {
          email: email.trim(),
          roles: [...this.editRoleUuids],
        }),
      'Whitelist entry updated.',
      () => this.cancelEditWhitelist()
    );
  }

  requestDeleteWhitelist(entry: WhitelistEntry) {
    this.pendingWhitelistDelete = entry;
    this.closeRoleDropdowns();
    this.successMessage = '';
    this.errorMessage = '';
  }

  cancelDeleteWhitelist() {
    if (this.loadingAction === 'whitelist-delete') {
      return;
    }
    this.pendingWhitelistDelete = null;
  }

  confirmDeleteWhitelist() {
    const entry = this.pendingWhitelistDelete;
    if (!entry) {
      return;
    }

    this.runWhitelistMutation(
      'whitelist-delete',
      () => this.auth.deleteWhitelistEntry(entry.uuid),
      'Whitelist entry deleted.',
      () => {
        this.pendingWhitelistDelete = null;
        if (this.editingWhitelistUuid === entry.uuid) {
          this.cancelEditWhitelist();
        }
      }
    );
  }

  private runWhitelistMutation(
    action: string,
    request: () => Observable<unknown>,
    successMessage: string,
    onSuccess?: () => void
  ) {
    this.loadingAction = action;
    this.successMessage = '';
    this.errorMessage = '';

    request().subscribe({
      next: () => {
        this.loadingAction = '';
        this.successMessage = successMessage;
        onSuccess?.();
        this.loadWhitelist(this.whitelistPage());
      },
      error: (message: string) => {
        this.loadingAction = '';
        this.errorMessage = message;
      },
    });
  }

  loadClientAccounts(page = this.clientAccountsPage()) {
    if (this.clientAccountsLoading()) {
      return;
    }

    this.clientAccountsLoading.set(true);
    this.loadingAction = 'client-accounts';
    this.errorMessage = '';

    this.auth
      .listClientAccounts({
        page,
        pageSize: this.clientAccountsPageSize,
      })
      .subscribe({
        next: (response) => {
          this.clientAccountsLoading.set(false);
          this.loadingAction = '';
          const total = response.count ?? 0;
          const responsePage = response.page ?? page;
          const totalPages = Math.max(
            1,
            Math.ceil(total / this.clientAccountsPageSize)
          );
          if (responsePage > totalPages) {
            this.loadClientAccounts(totalPages);
            return;
          }
          this.clientAccounts.set(response.data ?? []);
          this.clientAccountsTotal.set(total);
          this.clientAccountsPage.set(responsePage);
          if (response.availableRoles) {
            this.clientAvailableRoles.set(response.availableRoles);
          }
        },
        error: (message: string) => {
          this.clientAccountsLoading.set(false);
          this.loadingAction = '';
          this.errorMessage = message;
        },
      });
  }

  clientAccountsTotalPages() {
    return Math.max(
      1,
      Math.ceil(this.clientAccountsTotal() / this.clientAccountsPageSize)
    );
  }

  canGoClientAccountsPrev() {
    return this.clientAccountsPage() > 1;
  }

  canGoClientAccountsNext() {
    return this.clientAccountsPage() < this.clientAccountsTotalPages();
  }

  goClientAccountsPage(delta: number) {
    const next = this.clientAccountsPage() + delta;
    if (next < 1 || next > this.clientAccountsTotalPages()) {
      return;
    }
    this.cancelEditClientAccount();
    this.loadClientAccounts(next);
  }

  planName(account: ClientAccount) {
    return account.monthly_plan?.name || '—';
  }

  clientRoleNames(account: ClientAccount) {
    return (account.roles ?? []).map((role) => role.name).join(', ') || '—';
  }

  toDatetimeLocalValue(value?: string | null) {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  startEditClientAccount(account: ClientAccount) {
    this.editingClientAccountUuid = account.uuid;
    this.clientPatchForm.setValue({
      uuid: account.uuid,
      name: account.name || '',
      email: account.email || '',
      verified: !!account.verified,
      monthlyPlanId: account.monthly_plan_id ?? null,
      reqs: account.reqs ?? null,
      monthlyReqs: account.monthly_reqs ?? null,
      cycleStartAt: this.toDatetimeLocalValue(account.cycle_start_at),
      operationsPassword: '',
    });
    this.editClientRoleUuids = new Set(
      (account.roles ?? []).map((role) => role.uuid)
    );
    this.editClientRolesOpen = false;
    this.successMessage = '';
    this.errorMessage = '';
  }

  cancelEditClientAccount() {
    this.editingClientAccountUuid = null;
    this.editClientRoleUuids = new Set();
    this.editClientRolesOpen = false;
    this.clientPatchForm.reset({
      uuid: '',
      name: '',
      email: '',
      verified: false,
      monthlyPlanId: 1,
      reqs: 0,
      monthlyReqs: 0,
      cycleStartAt: '',
      operationsPassword: '',
    });
  }

  updateClientAccount() {
    if (!this.editingClientAccountUuid || this.clientPatchForm.invalid) {
      this.clientPatchForm.markAllAsTouched();
      return;
    }
    if (this.editClientRoleUuids.size === 0) {
      this.errorMessage = 'Select at least one role.';
      return;
    }

    const {
      uuid,
      name,
      email,
      verified,
      monthlyPlanId,
      reqs,
      monthlyReqs,
      cycleStartAt,
      operationsPassword,
    } = this.clientPatchForm.getRawValue();

    const patch: Record<string, unknown> = {
      name: name.trim(),
      email: email.trim(),
      verified,
      roles: [...this.editClientRoleUuids],
    };

    if (monthlyPlanId != null) {
      patch['monthly_plan'] = Number(monthlyPlanId);
    }
    if (reqs != null && String(reqs) !== '') {
      patch['reqs'] = Number(reqs);
    }
    if (monthlyReqs != null && String(monthlyReqs) !== '') {
      patch['monthly_reqs'] = Number(monthlyReqs);
    }
    if (cycleStartAt.trim()) {
      patch['cycle_start_at'] = new Date(cycleStartAt).toISOString();
    }
    if (operationsPassword.trim()) {
      patch['operationsPassword'] = operationsPassword;
    }

    this.loadingAction = 'client-account-update';
    this.successMessage = '';
    this.errorMessage = '';

    this.auth.updateClientAccount(uuid, patch).subscribe({
      next: () => {
        this.loadingAction = '';
        this.successMessage = 'Client account updated.';
        this.cancelEditClientAccount();
        this.loadClientAccounts(this.clientAccountsPage());
      },
      error: (message: string) => {
        this.loadingAction = '';
        this.errorMessage = message;
      },
    });
  }

  loadApiKeys() {
    this.runDashboardAction('api-keys', () =>
      this.auth.listApiKeys({ page: Number(this.apiKeysPageForm.getRawValue().page) })
    );
  }

  getApiKey() {
    this.runDashboardAction('api-key-lookup', () =>
      this.auth.getApiKey(this.apiKeyLookupForm.getRawValue().keyId)
    );
  }

  revokeApiKey() {
    const { keyId, operationsPassword } = this.apiKeyLookupForm.getRawValue();
    this.runDashboardAction('api-key-revoke', () =>
      this.auth.revokeApiKey(keyId, operationsPassword)
    );
  }

  loadRolesCatalog(page = this.catalogRolesPage()) {
    if (this.catalogRolesLoading()) {
      return;
    }

    this.catalogRolesLoading.set(true);
    this.loadingAction = 'roles-catalog';
    this.errorMessage = '';

    this.auth
      .listRolesCatalog({
        page,
        pageSize: this.catalogRolesPageSize,
        search: this.roleSearchForm.getRawValue().search,
      })
      .subscribe({
        next: (response) => {
          this.catalogRolesLoading.set(false);
          this.loadingAction = '';
          const total = response.count ?? 0;
          const responsePage = response.page ?? page;
          const totalPages = Math.max(
            1,
            Math.ceil(total / this.catalogRolesPageSize)
          );
          if (responsePage > totalPages) {
            this.loadRolesCatalog(totalPages);
            return;
          }
          this.catalogRoles.set(response.data ?? []);
          this.catalogRolesTotal.set(total);
          this.catalogRolesPage.set(responsePage);
          if (response.availablePermissions) {
            this.availablePermissions.set(response.availablePermissions);
          }
        },
        error: (message: string) => {
          this.catalogRolesLoading.set(false);
          this.loadingAction = '';
          this.errorMessage = message;
        },
      });
  }

  onRolesSearch() {
    if (this.rolesSearchTimer) {
      clearTimeout(this.rolesSearchTimer);
    }
    this.rolesSearchTimer = setTimeout(() => {
      this.catalogRolesPage.set(1);
      this.loadRolesCatalog(1);
    }, 300);
  }

  catalogRolesTotalPages() {
    return Math.max(
      1,
      Math.ceil(this.catalogRolesTotal() / this.catalogRolesPageSize)
    );
  }

  canGoCatalogRolesPrev() {
    return this.catalogRolesPage() > 1;
  }

  canGoCatalogRolesNext() {
    return this.catalogRolesPage() < this.catalogRolesTotalPages();
  }

  goCatalogRolesPage(delta: number) {
    const next = this.catalogRolesPage() + delta;
    if (next < 1 || next > this.catalogRolesTotalPages()) {
      return;
    }
    this.cancelEditCatalogRole();
    this.loadRolesCatalog(next);
  }

  rolePermissionNames(role: CatalogRole) {
    return (role.permissions ?? []).map((permission) => permission.name).join(', ') || '—';
  }

  startEditCatalogRole(role: CatalogRole) {
    this.editingCatalogRoleUuid = role.uuid;
    this.roleUpdateForm.setValue({
      uuid: role.uuid,
      name: role.name || '',
    });
    this.editPermissionUuids = new Set(
      (role.permissions ?? []).map((permission) => permission.uuid)
    );
    this.editPermissionsOpen = false;
    this.createPermissionsOpen = false;
    this.successMessage = '';
    this.errorMessage = '';
  }

  cancelEditCatalogRole() {
    this.editingCatalogRoleUuid = null;
    this.editPermissionUuids = new Set();
    this.editPermissionsOpen = false;
    this.roleUpdateForm.reset({ uuid: '', name: '' });
  }

  createCatalogRole() {
    if (this.roleCreateForm.invalid || this.loadingAction) {
      this.roleCreateForm.markAllAsTouched();
      return;
    }
    if (this.createPermissionUuids.size === 0) {
      this.errorMessage = 'Select at least one permission.';
      return;
    }

    const { name } = this.roleCreateForm.getRawValue();
    this.loadingAction = 'role-create';
    this.successMessage = '';
    this.errorMessage = '';

    this.auth
      .createRole({
        name: name.trim(),
        permissions: [...this.createPermissionUuids],
      })
      .subscribe({
        next: () => {
          this.loadingAction = '';
          this.successMessage = 'Role created.';
          this.roleCreateForm.reset({ name: '' });
          this.createPermissionUuids = new Set();
          this.createPermissionsOpen = false;
          this.loadRolesCatalog(this.catalogRolesPage());
        },
        error: (message: string) => {
          this.loadingAction = '';
          this.errorMessage = message;
        },
      });
  }

  updateCatalogRole() {
    if (!this.editingCatalogRoleUuid || this.roleUpdateForm.invalid) {
      this.roleUpdateForm.markAllAsTouched();
      return;
    }
    if (this.editPermissionUuids.size === 0) {
      this.errorMessage = 'Select at least one permission.';
      return;
    }

    const { uuid, name } = this.roleUpdateForm.getRawValue();
    this.loadingAction = 'role-update';
    this.successMessage = '';
    this.errorMessage = '';

    this.auth
      .updateRole(uuid, {
        name: name.trim(),
        permissions: [...this.editPermissionUuids],
      })
      .subscribe({
        next: () => {
          this.loadingAction = '';
          this.successMessage = 'Role updated.';
          this.cancelEditCatalogRole();
          this.loadRolesCatalog(this.catalogRolesPage());
        },
        error: (message: string) => {
          this.loadingAction = '';
          this.errorMessage = message;
        },
      });
  }

  loadPermissionsCatalog(page = this.catalogPermissionsPage()) {
    if (this.catalogPermissionsLoading()) {
      return;
    }

    this.catalogPermissionsLoading.set(true);
    this.loadingAction = 'permissions-catalog';
    this.errorMessage = '';

    this.auth
      .listPermissionsCatalog({
        page,
        pageSize: this.catalogPermissionsPageSize,
        search: this.permissionSearchForm.getRawValue().search,
      })
      .subscribe({
        next: (response) => {
          this.catalogPermissionsLoading.set(false);
          this.loadingAction = '';
          const total = response.count ?? 0;
          const responsePage = response.page ?? page;
          const totalPages = Math.max(
            1,
            Math.ceil(total / this.catalogPermissionsPageSize)
          );
          if (responsePage > totalPages) {
            this.loadPermissionsCatalog(totalPages);
            return;
          }
          this.catalogPermissions.set(response.data ?? []);
          this.catalogPermissionsTotal.set(total);
          this.catalogPermissionsPage.set(responsePage);
        },
        error: (message: string) => {
          this.catalogPermissionsLoading.set(false);
          this.loadingAction = '';
          this.errorMessage = message;
        },
      });
  }

  onPermissionsSearch() {
    if (this.permissionsSearchTimer) {
      clearTimeout(this.permissionsSearchTimer);
    }
    this.permissionsSearchTimer = setTimeout(() => {
      this.catalogPermissionsPage.set(1);
      this.loadPermissionsCatalog(1);
    }, 300);
  }

  catalogPermissionsTotalPages() {
    return Math.max(
      1,
      Math.ceil(
        this.catalogPermissionsTotal() / this.catalogPermissionsPageSize
      )
    );
  }

  canGoCatalogPermissionsPrev() {
    return this.catalogPermissionsPage() > 1;
  }

  canGoCatalogPermissionsNext() {
    return this.catalogPermissionsPage() < this.catalogPermissionsTotalPages();
  }

  goCatalogPermissionsPage(delta: number) {
    const next = this.catalogPermissionsPage() + delta;
    if (next < 1 || next > this.catalogPermissionsTotalPages()) {
      return;
    }
    this.cancelEditCatalogPermission();
    this.loadPermissionsCatalog(next);
  }

  startEditCatalogPermission(permission: CatalogPermission) {
    this.editingCatalogPermissionUuid = permission.uuid;
    this.permissionUpdateForm.setValue({
      uuid: permission.uuid,
      name: permission.name || '',
    });
    this.successMessage = '';
    this.errorMessage = '';
  }

  cancelEditCatalogPermission() {
    this.editingCatalogPermissionUuid = null;
    this.permissionUpdateForm.reset({ uuid: '', name: '' });
  }

  createCatalogPermission() {
    if (this.permissionCreateForm.invalid || this.loadingAction) {
      this.permissionCreateForm.markAllAsTouched();
      return;
    }

    const { name } = this.permissionCreateForm.getRawValue();
    this.loadingAction = 'permission-create';
    this.successMessage = '';
    this.errorMessage = '';

    this.auth.createPermission(name.trim()).subscribe({
      next: () => {
        this.loadingAction = '';
        this.successMessage = 'Permission created.';
        this.permissionCreateForm.reset({ name: '' });
        this.loadPermissionsCatalog(this.catalogPermissionsPage());
      },
      error: (message: string) => {
        this.loadingAction = '';
        this.errorMessage = message;
      },
    });
  }

  updateCatalogPermission() {
    if (!this.editingCatalogPermissionUuid || this.permissionUpdateForm.invalid) {
      this.permissionUpdateForm.markAllAsTouched();
      return;
    }

    const { uuid, name } = this.permissionUpdateForm.getRawValue();
    this.loadingAction = 'permission-update';
    this.successMessage = '';
    this.errorMessage = '';

    this.auth.updatePermission(uuid, name.trim()).subscribe({
      next: () => {
        this.loadingAction = '';
        this.successMessage = 'Permission updated.';
        this.cancelEditCatalogPermission();
        this.loadPermissionsCatalog(this.catalogPermissionsPage());
      },
      error: (message: string) => {
        this.loadingAction = '';
        this.errorMessage = message;
      },
    });
  }

  resultTables(value: unknown): DashboardTable[] {
    const tables: DashboardTable[] = [];
    this.collectTables(this.unwrapResultPayload(value), 'Result', tables);
    return tables;
  }

  hasResultTables(value: unknown) {
    return this.resultTables(value).length > 0;
  }

  formatColumn(column: string) {
    return column
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  formatCell(value: unknown): string {
    if (value == null) return '';
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (this.isObjectRecord(item)) {
            return String(item['name'] ?? item['uuid'] ?? JSON.stringify(item));
          }
          return String(item);
        })
        .join(', ');
    }
    if (value instanceof Date) {
      return this.formatAdminDateTime(value.toISOString());
    }
    if (typeof value === 'string' && this.looksLikeIsoDate(value)) {
      return this.formatAdminDateTime(value);
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  private looksLikeIsoDate(value: string) {
    if (
      !/^\d{4}-\d{2}-\d{2}([T\s]\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z|[+-]\d{2}:?\d{2})?)?$/.test(
        value
      )
    ) {
      return false;
    }
    return !Number.isNaN(new Date(value).getTime());
  }

  logout() {
    if (this.loadingAction) {
      return;
    }

    this.loadingAction = 'logout';
    this.auth.logout().subscribe({
      next: () => this.router.navigate(['/login']),
    });
  }

  private runAction(
    action: string,
    request: () => Observable<ApiResponse>,
    fallbackMessage?: string
  ) {
    this.loadingAction = action;
    this.successMessage = '';
    this.errorMessage = '';
    this.copyMessage = '';

    request().subscribe({
      next: (response) => {
        this.loadingAction = '';
        this.successMessage =
          fallbackMessage || response.message || 'Changes saved.';
        if (action === 'password') {
          this.clearPasswordFields();
        }
      },
      error: (message: string) => {
        this.loadingAction = '';
        this.errorMessage = message;
      },
    });
  }

  private runDashboardAction(
    action: string,
    request: () => Observable<unknown>,
    successMessage = 'Request completed.'
  ) {
    this.loadingAction = action;
    this.successMessage = '';
    this.errorMessage = '';
    this.copyMessage = '';

    request().subscribe({
      next: (response) => {
        this.loadingAction = '';
        this.successMessage = successMessage;
        this.dashboardResults = { [action]: response };
      },
      error: (message: string) => {
        this.loadingAction = '';
        this.errorMessage = message;
      },
    });
  }

  private parseJsonControl(value: string):
    | { ok: true; value: unknown }
    | { ok: false; message: string } {
    try {
      return { ok: true, value: JSON.parse(value) };
    } catch {
      return { ok: false, message: 'Enter valid JSON before submitting.' };
    }
  }

  private isObjectRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private unwrapResultPayload(value: unknown) {
    if (!this.isObjectRecord(value)) {
      return value;
    }

    if ('data' in value && value['data'] != null) {
      return value['data'];
    }

    return value;
  }

  private collectTables(
    value: unknown,
    title: string,
    tables: DashboardTable[]
  ) {
    if (Array.isArray(value)) {
      const rows = value.map((item) =>
        this.isObjectRecord(item) ? item : { value: item }
      );
      if (rows.length > 0) {
        tables.push({
          title,
          columns: this.columnsForRows(rows),
          rows,
        });
      }
      return;
    }

    if (!this.isObjectRecord(value)) {
      return;
    }

    const scalarRow: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      if (Array.isArray(entry)) {
        this.collectTables(entry, this.formatColumn(key), tables);
      } else if (this.isObjectRecord(entry)) {
        this.collectTables(entry, this.formatColumn(key), tables);
      } else {
        scalarRow[key] = entry;
      }
    }

    if (Object.keys(scalarRow).length > 0) {
      tables.unshift({
        title,
        columns: ['property', 'value'],
        rows: Object.entries(scalarRow).map(([property, entry]) => ({
          property: this.formatColumn(property),
          value: entry,
        })),
      });
    }
  }

  private columnsForRows(rows: Record<string, unknown>[]) {
    const columns: string[] = [];
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (!columns.includes(key)) {
          columns.push(key);
        }
      }
    }
    return columns;
  }

  private syncSelfServiceForm() {
    const user = this.user();
    this.selfServiceSnapshot = {
      name: user?.name ?? '',
      email: user?.email ?? '',
      monthlyPlanId: this.selfServiceSnapshot.monthlyPlanId,
    };
    this.selfServiceForm.patchValue({
      name: this.selfServiceSnapshot.name,
      email: this.selfServiceSnapshot.email,
      monthlyPlanId: this.selfServiceSnapshot.monthlyPlanId,
    });
  }

  private clearPasswordFields() {
    this.selfServiceForm.controls.oldPassword.reset('');
    this.selfServiceForm.controls.password.reset('');
    this.selfServiceForm.controls.confirmPassword.reset('');
    this.selfServiceForm.controls.confirmPassword.setErrors(null);
  }
}
